import {
  FREE_SHIPPING_MONTHS,
  PRIZE_COUPON_TYPES,
} from "../../../../constants";
import { IPrizeCoupon } from "../../../../types";

export interface CouponGroups {
  lottery: IPrizeCoupon[];
  freeShipping: IPrizeCoupon[];
}

export type CouponAvailability = "copy" | "not-issued" | "expired" | "used";

export interface LosAngelesDate {
  month: number;
  year: number;
}

const validCouponTypes = new Set<number>(Object.values(PRIZE_COUPON_TYPES));

const getCouponDeduplicationKey = (
  coupon: IPrizeCoupon,
  index: number,
): string => {
  if (coupon.shopify_discount_code_id !== undefined) {
    return `discount-code-id:${coupon.shopify_discount_code_id}`;
  }

  if (coupon.shopify_discount_code) {
    return `discount-code:${coupon.shopify_discount_code}`;
  }

  // Keep malformed rows separate so that an absent key never merges coupons.
  return `row:${index}`;
};

const isValidCoupon = (coupon: IPrizeCoupon): boolean => {
  if (
    coupon.discount_code_type === undefined ||
    !validCouponTypes.has(coupon.discount_code_type)
  ) {
    return false;
  }

  if (coupon.discount_code_type === PRIZE_COUPON_TYPES.FREE_SHIPPING) {
    return coupon.free_shipping_type === 1 ||
      coupon.free_shipping_type === 2 ||
      coupon.free_shipping_type === 3
      ? true
      : false;
  }

  return typeof coupon.m === "number" && typeof coupon.n === "number";
};

export const deduplicateAndGroupCoupons = (
  coupons: IPrizeCoupon[],
): CouponGroups => {
  const seen = new Set<string>();

  return coupons.reduce<CouponGroups>(
    (groups, coupon, index) => {
      if (!isValidCoupon(coupon)) {
        return groups;
      }

      const key = getCouponDeduplicationKey(coupon, index);
      if (seen.has(key)) {
        return groups;
      }
      seen.add(key);

      if (coupon.discount_code_type === PRIZE_COUPON_TYPES.FREE_SHIPPING) {
        groups.freeShipping.push(coupon);
      } else {
        groups.lottery.push(coupon);
      }

      return groups;
    },
    { lottery: [], freeShipping: [] },
  );
};

export const getLosAngelesDate = (now = new Date()): LosAngelesDate => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "numeric",
    year: "numeric",
  }).formatToParts(now);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const year = Number(parts.find((part) => part.type === "year")?.value);

  return { month, year };
};

export const getFreeShippingMonth = (
  freeShippingType?: number,
): number | undefined => {
  if (
    freeShippingType !== 1 &&
    freeShippingType !== 2 &&
    freeShippingType !== 3
  ) {
    return undefined;
  }

  return FREE_SHIPPING_MONTHS[freeShippingType];
};

export const getFreeShippingAvailability = (
  freeShippingType: number | undefined,
  currentMonth: number,
): CouponAvailability => {
  const couponMonth = getFreeShippingMonth(freeShippingType);

  if (couponMonth === undefined || couponMonth > currentMonth) {
    return "not-issued";
  }

  if (couponMonth < currentMonth) {
    return "expired";
  }

  return "copy";
};

